import useLanguage from "@casino/legacy/client/hooks/useLanguage";
import useCurrentUser from "@casino/legacy/client/hooks/user/useCurrentUser";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import parsePhoneNumber from "../helpers/parsePhoneNumber";
import useCallingCodes from "./useCallingCodes";
import useUserGeoLocation from "./useUserGeoLocation";

type Props = {
  value?: string;
  excludedCountries?: string[];
};

const usePhoneDetails = ({ value = "", excludedCountries }: Props) => {
  const { userGeoCountryCode } = useUserGeoLocation();
  const { getCountryCallingCode, formatPhoneCallingCode } = useCallingCodes();
  const { profile, countryInfo } = useCurrentUser();
  const phoneDetails = parsePhoneNumber(
    value || !profile?.phone ? value : profile?.phone,
  );
  const { code: language } = useLanguage();
  const suggestedCountryCode = language === "ja" ? "JP" : userGeoCountryCode;
  const countryCodeFromPathOrGeo = excludedCountries?.includes(
    suggestedCountryCode,
  )
    ? ""
    : suggestedCountryCode;
  const phoneCountry =
    phoneDetails?.country ??
    countryInfo?.countryCode ??
    countryCodeFromPathOrGeo;

  const registeredCallingCode = getCountryCallingCode(phoneCountry);

  const [countryCode, setCountryCode] = useState(phoneCountry);
  const [countryNumber, setCountryNumber] = useState(registeredCallingCode);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(
    phoneDetails?.number,
  );

  useEffect(() => {
    if (!countryCode) {
      if (countryCodeFromPathOrGeo) {
        setCountryCode(countryCodeFromPathOrGeo);
        const callingCode = getCountryCallingCode(countryCodeFromPathOrGeo);
        setCountryNumber(callingCode);
      }
    }
  }, [countryCode, getCountryCallingCode, countryCodeFromPathOrGeo]);

  useEffect(() => {
    const parsedPhone = parsePhoneNumberFromString(phoneNumber ?? "");
    if (phoneNumber?.startsWith("+")) {
      const country = parsedPhone?.country;
      if (country) {
        setCountryCode(country);
        const { phone, code } = formatPhoneCallingCode({
          country,
          callingCode: parsedPhone.countryCallingCode,
          phone: parsedPhone.number.toString(),
        });
        setPhoneNumber(phone);
        setCountryNumber(code);
      }
    }
  }, [phoneNumber, formatPhoneCallingCode, countryCode, countryNumber]);

  const handleNumberChange = (event: FormEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const nationalNumber = event.currentTarget.value;
    setPhoneNumber(nationalNumber);
  };

  const handleCodeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const code = event.currentTarget.value;
    setCountryCode(code);
  };

  useEffect(() => {
    setCountryNumber(getCountryCallingCode(countryCode));
  }, [countryCode, setCountryNumber, getCountryCallingCode]);

  return {
    countryCode,
    countryNumber,
    phoneNumber,
    handleCodeChange,
    handleNumberChange,
    setCountryCode,
  };
};

export default usePhoneDetails;
